export const meta = {
  id: '19-locative',
  order: 19,
  titleFi: 'Lokatiivi',
  titleEn: 'Locative',
  duolingoUnit: 'duo-21-locative',
  // ezīmītsos: March's Halloween line (hl-2027-03, together/halloween); with the rising pace (2026-10-14) the quotas
  // would bring it only in lesson 24
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['hae', 'kisalbar', 'zaldrīzes', 'brōzi', 'valyria', 'qintir', 'tubī', 'qilōny', 'rudirys', 'qurdon', 'dēmagon', 'kēli', 'ilagon', 'ezīmītsos', 'ours:ilvurlion', 'ours:pālelion', 'ours:tārturlion', 'ours:ojārvos', 'ours:qanurlion', 'ours:harrōñe', 'ours:vumbiarzītsos', 'ours:suomi', 'ours:italia', 'ours:suēdeni', 'ours:tallinni', 'ours:lappi', 'ours:treni', 'ours:uglaesiarzy', 'ours:adresi', 'ours:studija', 'ours:jurnerurlion', 'ours:kontēa'],
  exampleIds: ['ex-7114850d3b30', 'ex-51968cccd9d6', 'got-s3e04-012', 'ex-817e74fec498', 'ex-1355f15072e1', 'ex-b81f75012d63', 'got-s3e04-014', 'ex-6177b5cca8d0', 'got-s3e06-003', 'got-s3e07-001', 'ex-a0843f95362d'],
  checkFocus: { cases: ['loc'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Locative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Locative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Prepositions',
    'https://wiki.languageinvention.com/index.php?title=Ilagon',
    'https://wiki.languageinvention.com/index.php?title=Hae',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['cases', 'adj-position'],
  drills: { cases: ['loc'] },
  showLines: ['got-s3e04-012', 'got-s3e04-014', 'got-s3e06-003', 'got-s3e07-001'],
  pronunciation: { focus: 'stress', words: ['kisalbar', 'zaldrīzes', 'Valyria', 'qilōny', 'rudirys'], lines: ['got-s3e04-012', 'got-s3e04-014', 'got-s3e06-003'] },
  letter: {
    fi: 'Kerro, missä olet nyt ja missä ystäväsi asuvat (lokatiivi).',
    en: 'Write where you are now and where your friends live (locative).',
  },
  scene: 'got-s3e07-1',
}

# Lokatiivi

Suomalainen on paikallissijojen mestari: talossa, talosta, taloon, pöydällä, pöydältä, pöydälle. Korkeavalyria tulee toimeen paljon vähemmällä. Paikkaa varten on yksi sija, **lokatiivi**, joka vastaa sekä inessiiviä että adessiivia – sekä ’jossakin’ että ’jonkin päällä’. Tällä oppitunnilla opit muodostamaan lokatiivin, valitsemaan oikean olla-verbin, kun puhut paikasta, ja käyttämään prepositiota <Hv glossed>hae</Hv> ’kuin, jonakin’.

## Missä? Lokatiivi paikkana

Lokatiivi merkitsee paikkaa, jossa jotakin tapahtuu: *poika söi leivoksen nurmikolla*, *tyttö asui talossa*. Lisäksi lokatiivia käytetään tiettyjen verbien objektina (niihin palaamme myöhemmin) sekä preposition <Hv glossed>hae</Hv> ’kuin’ jälkeen.

<Compare fi="Valyria|ssa" hv="Valyr|iā" label="inessiivi ~ lokatiivi" />

<Compare fi="pöydä|llä" hv="qurd|ot" label="adessiivi ~ lokatiivi" />

Yksi lokatiivi kattaa siis sekä ’-ssa’ että ’-lla’. Kumpaa tarkoitetaan, selviää asiayhteydestä. Duolingon Tips & Notesin esimerkki on mainio:

- <Hv glossed>Taoba qurdot dēmas.</Hv> ’Poika istuu pöydän ääressä.’ – koska ihmiset tavallisesti istuvat pöydän ääressä.
- <Hv glossed>Kēli qurdot dēmas.</Hv> ’Kissa istuu pöydällä.’ – koska kissat ovat parantumattomia.

<Interlinear hv="Kēl|i qurd|ot dēma|s." gloss="kissa-NOM pöytä-LOC istua-PRS.3SG" fi="Kissa istuu pöydällä." />

Suomesta ei tässä ole apua, pikemminkin päinvastoin: suomeksi on valittava *pöydän ääressä*, *pöydällä* tai *pöydässä*, valyriaksi ei. Kun käännät valyriasta, valitse se suomen paikallissija, joka on tilanteeseen luontevin.

<Example id="ex-7114850d3b30" />

## Näin lokatiivi muodostetaan

Lokatiivin yksiköllä on kaksi perustyyppiä.

**1. Pidentynyt vokaali.** Monessa sanassa lokatiivi syntyy, kun päätteen teemavokaali pitenee. Duolingon ohjeissa päätteet luetellaan näin:

- <Hv>-a</Hv>: <Hv>vala</Hv> → <Hv glossed>valā</Hv> ’miehen päällä’
- <Hv>-ar</Hv>: <Hv>kisalbar</Hv> → <Hv glossed>kisalbār</Hv> ’pidoissa’
- <Hv>-e</Hv>: <Hv>anne</Hv> → <Hv glossed>annē</Hv> ’hevosen selässä’
- <Hv>-es</Hv>: <Hv>zaldrīzes</Hv> → <Hv glossed>zaldrīzē</Hv> ’lohikäärmeen selässä’
- <Hv>-i</Hv>: <Hv l="brōzi">brōzi</Hv> → <Hv glossed>brōzī</Hv> ’nimessä’
- <Hv>-ia</Hv>: <Hv>Valyria</Hv> → <Hv glossed>Valyriā</Hv> ’Valyriassa’
- <Hv>-ien</Hv>: <Hv>Targārien</Hv> → <Hv glossed>Targāriēn</Hv> ’Targaryenin päällä’
- <Hv>-ir</Hv>: <Hv>qintir</Hv> → <Hv glossed>qintīr</Hv> ’kilpikonnan päällä’
- <Hv>-is</Hv>: <Hv>tubis</Hv> → <Hv glossed>tubī</Hv> ’päivänä’ eli ’tänään’
- <Hv>-y</Hv>: <Hv>qilōny</Hv> → <Hv glossed>qilōnȳ</Hv> ’piiskan päällä’
- <Hv>-ys</Hv>: <Hv>rudirys</Hv> → <Hv glossed>rudirȳ</Hv> ’kolossa’

Huomaa, että <Hv>-es</Hv>-, <Hv>-is</Hv>- ja <Hv>-ys</Hv>-sanoista loppu-<Hv>s</Hv> katoaa.

**2. Sama kuin datiivi.** Kaikissa muissa sanoissa lokatiivi on samannäköinen kuin datiivi: <Hv>qurdon</Hv> → datiivi <Hv glossed>qurdot</Hv> ’pöydälle’ → lokatiivi <Hv glossed>qurdot</Hv> ’pöydällä’. Datiivin osaat jo, joten tässä ei ole mitään uutta opeteltavaa.

Tässä suomesta on todella apua. Suomalaiselle vokaalin pituus erottaa sanoja (*tuli* ~ *tuuli*, *muta* ~ *mutta*), joten ero <Hv>vala</Hv> ~ <Hv>valā</Hv> kuuluu korvaan heti. Englanninpuhujalle se on vaikea, sinulle luonteva. Pidä vain pitkä vokaali pitkänä myös silloin, kun se on painottomassa tavussa.

<Paradigm lemma="kisalbar" cases="nom,gen,dat,loc" numbers="sg,pl" />

<Paradigm lemma="qilōny" cases="nom,gen,dat,loc" numbers="sg,pl" />

**Monikko** on vielä helpompi. Lähes kaikissa sanoissa monikon lokatiivi on sama kuin monikon datiivi ja genetiivi. Ainoat poikkeukset ovat <Hv>-y</Hv>- ja <Hv>-ys</Hv>-loppuiset sanat, joilla on oma monikon lokatiivi: <Hv glossed>qilōnȳ</Hv> ’piiskan päällä’ mutta <Hv glossed>qilōnī</Hv> ’piiskojen päällä’, <Hv glossed>rudirȳ</Hv> ’kolossa’ mutta <Hv glossed>rudirī</Hv> ’koloissa’.

<Example id="ex-51968cccd9d6" />

## Aika on myös paikka

Lokatiivi ilmaisee myös ajankohdan. Tämän oppitunnin sana <Hv glossed>tubī</Hv> ’tänään’ on muodoltaan sanan <Hv glossed>tubis</Hv> ’päivä’ lokatiivi: ’päivänä’. Suomessa ajankohta on usein essiivissä tai adessiivissa (*tänä päivänä*, *maanantaina*, *illalla*), joten ajatus on tuttu.

<Compare glossed fi="tänä päivä|nä" hv="kesȳ tub|ī" label="essiivi ~ lokatiivi: ajankohta" />

Daenerys käyttää sitä Astaporissa vapauttaessaan Tahrattomat:

<Line id="got-s3e04-012" />

Repliikissä on paljon tuttua: <Hv glossed>jemot</Hv> ’teille’ on datiivi, ja <Hv glossed>kesȳ</Hv> ’tänä’ mukautuu aurinkosukuiseen sanaan <Hv>tubis</Hv> ja on lokatiivissa. Pidentynyt <Hv>ȳ</Hv> on sama ilmiö kuin oppitunnin lopun adjektiivitaulukoissa.

<Pronounce word="kisalbār" />

<Pronounce word="qilōny" />

## Olla jossakin: ilagon eikä sagon

Kun olla-verbin täydennys on lokatiivissa eli kun sanot, että jokin **on jossakin**, verbi ei ole <Hv>sagon</Hv> vaan <Hv>ilagon</Hv>. Sen perusmerkitys on ’maata, sijaita’, mutta suomeksi se on useimmiten parasta kääntää yksinkertaisesti olla-verbillä. Eron näit jo <LessonLink n={3}>oppitunnilla 3</LessonLink>.

- <Hv glossed>Havon qurdot ilza.</Hv> ’Leipä on pöydällä.’

Samalla verbillä muodostetaan myös **eksistentiaalilause**: sama valyrian lause voi tarkoittaa ’Pöydällä on leipää’. Suomalaiselle tämä on tuttua. Meilläkin eksistentiaalilause alkaa usein paikasta, ja ainesana on siinä partitiivissa, kuten ruoan oppitunnilla opit.

<Paradigm lemma="ilagon" filter="indicative.active.present" numbers="sg,pl" />

<Example id="ex-817e74fec498" />

<Example id="ex-1355f15072e1" />

<Example id="ex-b81f75012d63" />

Viimeisessä esimerkissä paikka on <Hv glossed>tolviot</Hv> ’kaikkialla’, ja verbi on monikossa (<Hv l="ilagon">ilzi</Hv>), koska Tahrattomia on monta.

## Hae: kuin, jonakin

Kolmas prepositio, <Hv>hae</Hv>, on aina lokatiivin kanssa. Se tarkoittaa ’kuin, kuten, jonkin kaltainen’ ja myös ’jonkin roolissa, jonakin’. Jälkimmäinen merkitys on suomeksi usein essiivi:

<Line id="got-s3e04-014" />

<Hv glossed>Hae dāero valoti</Hv> on suomeksi *vapaina miehinä* – juuri essiivi. Kumpikin sana on monikon lokatiivissa: <Hv>valoti</Hv> on sanan <Hv>vala</Hv> monikon lokatiivi (sama kuin datiivi ja genetiivi), ja <Hv l="dāez">dāero</Hv> on adjektiivin etuasemainen muoto.

<Compare glossed fi="miehi|nä" hv="hae val|oti" label="essiivi ~ hae + lokatiivi" />

Punainen papitar käyttää samaa rakennetta pronominin kanssa – <Hv>jemē</Hv> on te-pronominin lokatiivi:

<Example id="ex-6177b5cca8d0" />

<Note>
Wikin kielioppisivun mukaan kaikkien kolmen preposition kanssa voi olla lokatiivi. Datiivin oppitunnilla <Hv>va</Hv> ja <Hv>hen</Hv> olivat datiivin kanssa. Lokatiivin kanssa <Hv glossed>va</Hv> tarkoittaa ’kohti, luona, lähellä’ ja <Hv glossed>hen</Hv> ’-sta, -lta’:

<Line id="got-s3e07-001" />

<Example id="ex-a0843f95362d" />

Thoros taas puhuu Jokimaista lokatiivilla <Hv>Qelbriā</Hv> kielteisessä lauseessa, jossa on subjunktiivi ja <Hv>daor</Hv>:

<Line id="got-s3e06-003" />
</Note>

## Adjektiivit lokatiivissa

Adjektiivien lokatiivi noudattaa yleensä substantiivien mallia: jos substantiivin lokatiivissa on pidentynyt vokaali, niin on adjektiivissakin, ja jos substantiivin lokatiivi on sama kuin datiivi, niin on adjektiivinkin. Lisäksi pätevät kaikki datiivin oppitunnin säännöt: **jälkiasemassa** muoto on pidempi, **etuasemassa** lyhyempi, ja yksikön pelkkään <Hv>t</Hv>:hen päättyvät muodot (paitsi <Hv>-ȳrot</Hv>) saavat <Hv>t</Hv>:nsä takaisin vokaalilla alkavan sanan edellä.

Jälkiasema:

<Paradigm lemma="kasta" filter="positive.post" cases="loc" numbers="sg,pl" />

<Paradigm lemma="adere" filter="positive.post" cases="loc" numbers="sg,pl" />

<Paradigm lemma="gevie" filter="positive.post" cases="loc" numbers="sg,pl" />

Etuasema:

<Paradigm lemma="kasta" filter="positive.pre" cases="loc" numbers="sg,pl" />

<Paradigm lemma="adere" filter="positive.pre" cases="loc" numbers="sg,pl" />

<Paradigm lemma="gevie" filter="positive.pre" cases="loc" numbers="sg,pl" />

Katso erityisesti <Term id="adjektiiviluokka">I-luokan</Term> kuu- ja aurinkosukua: yksikössä on <Hv>kastā</Hv> ja <Hv l="kasta">kastȳ</Hv> – pidentynyt vokaali kuten sanoissa <Hv>valā</Hv> ja <Hv l="tubis">tubī</Hv> – ja sama muoto kummassakin asemassa. Suomesta on tässäkin apua vain puoliksi: adjektiivi mukautuu sijassa kuten suomessa (*isossa talossa*), mutta se, että muoto riippuu paikasta, on opeteltava.

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Practice hv="Kēli qurdot ilza." fi="Pöydällä on kissa." />

<Practice hv="Taoba annē dēmas." fi="Poika istuu hevosen selässä." />

<Practice hv="Zaldrīzes Valyriā ilza." fi="Lohikäärme on Valyriassa." />

<Practice hv="Havon lōgrot kastrot ilza." fi="Leipä on sinisessä laivassa." />

<Practice hv="Taoba hae zaldrīzē ipradas." fi="Poika syö kuin lohikäärme." />

Neljännessä lauseessa adjektiivi on jälkiasemassa, joten se on pitkässä muodossa <Hv>kastrot</Hv>. Etuasemassa sama olisi <Hv fi="sinisessä laivassa">kastro lōgrot</Hv>.

## Sanat

<Vocab />

<Check />
